import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { ThemeProvider } from 'next-themes';
import { JotaiProvider } from './lib/jotai-provider';
import { initSentry } from './lib/sentry/sentry.config.ts';
import { initPerformanceMonitoring, cleanupPerformanceMonitoring } from './lib/sentry/sentry-performance.ts';
import { setUserContext } from './lib/sentry/sentry-user-context.ts';
import { currentUserAtom } from './hooks/use-auth';
import { dropRetiredStorage } from './lib/account-storage';
import { hardReload } from './lib/hard-reload';
import { queryClient } from './lib/query-client';
import { businessUnitsQueryFor } from './hooks/use-business-units';
import { getDefaultStore } from 'jotai';

// Shed the keys older versions of this app wrote and none of it reads.
dropRetiredStorage();

/**
 * A page loaded on demand is a file named after its build. After a deploy that file is gone, so
 * a tab opened before it fails the moment it navigates to such a page and lands on the error
 * screen. Vite reports the failed load here: reload onto the new build instead. Once a minute
 * at most, so a file that is missing for another reason cannot loop the page.
 */
const RELOADED_AT_KEY = 'stale-build-reloaded-at';
window.addEventListener('vite:preloadError', (event) => {
	const last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
	if (Date.now() - last < 60_000) return;
	sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
	event.preventDefault();
	void hardReload();
});

// Initialize Sentry before React render
// Router will be passed later in App.tsx if needed
initSentry();

// Initialize performance monitoring
initPerformanceMonitoring();

/**
 * Start the brand list HERE, before React renders.
 *
 * `ProtectedRoute` holds the whole authenticated tree until this resolves, because a write
 * made while the brand is unknown is stored with `bu_id` NULL and becomes unreachable. That
 * gate is correct, but it is only as fast as the request behind it — and until now the
 * request did not start until the router had mounted, which means AFTER the bundle has
 * downloaded and parsed. App.tsx lazy-loads nothing, so that is the whole bundle.
 *
 * Firing it here overlaps the round trip with React booting instead of queueing it behind
 * it. Same request either way (shared `businessUnitsQuery` options, so react-query matches
 * it by key and the atom joins this one) — it just no longer waits for work it does not
 * depend on.
 *
 * Only with a token: without one the app renders the login page, which needs no brand, and
 * an unauthenticated call would 401 and poison the cache entry.
 *
 * Deliberately not awaited. This is a head start, not a barrier — the gate in
 * `ProtectedRoute` is what actually waits, and it is the place that knows what to render
 * meanwhile. `prefetchQuery` never rejects, so there is nothing to catch.
 */
// One read of the signed-in user, shared by the prefetch below and the Sentry context.
const store = getDefaultStore();
const bootUser = store.get(currentUserAtom);
if (localStorage.getItem('accessToken') && bootUser) {
  void queryClient.prefetchQuery(businessUnitsQueryFor(bootUser.accountId));
}

// Set initial user context if user is already logged in
if (bootUser) {
  setUserContext(bootUser);
}

// Cleanup performance monitoring on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cleanupPerformanceMonitoring();
  });
}

const root = createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    {/* `.dark` must land on <html>: index.css derives every colour from variables redefined there. */}
    <ThemeProvider attribute='class' defaultTheme='system' enableSystem disableTransitionOnChange>
      <JotaiProvider>
        <App />
      </JotaiProvider>
    </ThemeProvider>
  </StrictMode>
);

// Cleanup on app unmount (if root.unmount is called)
// Note: This is a fallback - beforeunload should handle most cases
if (typeof root.unmount === 'function') {
  // Store original unmount if needed for cleanup
  const originalUnmount = root.unmount;
  root.unmount = function() {
    cleanupPerformanceMonitoring();
    return originalUnmount.call(this);
  };
}
